import React from "react";
import { Group, Loader, Select } from "@mantine/core";
import { useCustomization } from "./_utils/CustomizationContext";

interface ObjectAttributeSelectProps {
  objectValue: string;
  onObjectChange: (value: string) => void;
  attributeValue: string;
  onAttributeChange: (value: string) => void;
  objectPlaceholder?: string;
  attributePlaceholder?: string;
  disabled?: boolean;
}

export const ObjectAttributeSelect: React.FC<ObjectAttributeSelectProps> = ({
  objectValue,
  onObjectChange,
  attributeValue,
  onAttributeChange,
  objectPlaceholder = "Select object",
  attributePlaceholder = "Select attribute",
  disabled = false,
}) => {
  const { customizationData, loading, error } = useCustomization();

  // Ensure we have valid data to work with
  const objectOptions = React.useMemo(() => {
    if (!customizationData) return [];
    return Object.keys(customizationData).map(key => ({
      value: key,
      label: key
    }));
  }, [customizationData]);

  const attributeOptions = React.useMemo(() => {
    if (!customizationData || !objectValue) return [];
    const attributes = customizationData[objectValue] || [];
    return attributes.map(attr => ({
      value: attr,
      label: attr
    }));
  }, [customizationData, objectValue]);

  return (
    <Group grow>
      <Select
        data={objectOptions}
        sx={{
          "input:disabled": {
            color: "black",
            fontWeight: 600,
          },
        }}
        rightSection={loading.fetch && <Loader size={14} />}
        placeholder={objectPlaceholder}
        value={objectValue || null}
        onChange={(value) => onObjectChange(value || '')}
        disabled={disabled}
        searchable
        clearable
      />
      <Select
        data={attributeOptions}
        sx={{
          "input:disabled": {
            color: "black",
            fontWeight: 600,
          },
        }}
        rightSection={loading.fetch && <Loader size={14} />}
        placeholder={attributePlaceholder}
        value={attributeValue || null}
        onChange={(value) => onAttributeChange(value || '')}
        disabled={!customizationData?.[objectValue] || disabled}
        searchable
        clearable
      />
    </Group>
  );
};
